import React, { MutableRefObject } from 'react';
import {
  ReactZoomPanPinchContentRef,
  ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch';
import { SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';

import { ProductExtraDescriptionList, ProductPurchaseNotice } from '@domains/pdp-common';

import { DESCRIPTION_CLASS_NAME } from '../constants/description.constant';
import { ExtraDescriptionContainer } from '../containers/ExtraDescriptionContainer';
import { DescToggleButton } from './DescToggleButton';
import { FoldableComponent } from './FoldableComponent';
import { PurchaseNotice } from './PurchaseNotice';

interface Props {
  zoomRef: React.Ref<ReactZoomPanPinchContentRef>;
  descriptionRef: MutableRefObject<HTMLDivElement | null>;
  style: {
    inlineStyle?: SerializedStyles | null;
    extraStyle?: SerializedStyles | null;
    defaultStyle: SerializedStyles | null;
  };
  action: {
    onZoomStop: (ref: ReactZoomPanPinchRef) => void;
    handleDescToggleButtonClick: () => void;
    customTransform: (x: number, y: number, scale: number) => string;
  };
  content: {
    description: string | JSX.Element | JSX.Element[];
    top_description_key_list: string[];
    bottom_description_key_list: string[];
    extra_description_list: ProductExtraDescriptionList[] | null;
    crema_fit_url_list: string[];
    purchase_notice: ProductPurchaseNotice | null;
  };
  display: {
    isZbrowser: boolean;
    panningDisabled: boolean;
  };
  initialState?: boolean;
  hide_toggle_buttons?: boolean;
  is_raffle?: boolean;
}

export const ProductDescription: React.FC<React.PropsWithChildren<Props>> = ({
  zoomRef,
  descriptionRef,
  style,
  action,
  content: {
    description,
    top_description_key_list,
    bottom_description_key_list,
    extra_description_list,
    crema_fit_url_list,
    purchase_notice,
  },
  display: { isZbrowser, panningDisabled },
  initialState,
  hide_toggle_buttons,
  is_raffle,
}) => {
  if (!description) {
    return <DescriptionLoading />;
  }

  return (
    <FoldableComponent
      opener={hide_toggle_buttons ? null : <DescToggleButton is_opener={true} />}
      closer={hide_toggle_buttons ? null : <DescToggleButton is_opener={false} />}
      onToggle={action.handleDescToggleButtonClick}
      initialState={initialState}
    >
      <GoodsExtraInfo ref={descriptionRef} className='goods_extra_info' isZbrowser={isZbrowser} is_raffle={is_raffle}>
        <TransformWrapper
          ref={zoomRef}
          maxScale={2}
          minScale={1}
          disablePadding={true}
          wheel={{ wheelDisabled: true }}
          doubleClick={{ disabled: true }}
          panning={{ disabled: panningDisabled }}
          pinch={{ step: 100 }}
          zoomAnimation={{ animationType: 'easeOutQuint' }}
          onZoomStop={action.onZoomStop}
          customTransform={action.customTransform}
        >
          <TransformComponent>
            <Wrap
              className={DESCRIPTION_CLASS_NAME}
              inlineStyle={style.inlineStyle}
              defaultStyle={style.defaultStyle}
              extraStyle={style.extraStyle}
              is_raffle={is_raffle}
            >
              <div>
                {top_description_key_list.map((key, index) => (
                  <ExtraDescriptionContainer
                    key={`${key}-${index}`}
                    extra_description_list={extra_description_list}
                    display_description_key={key}
                  />
                ))}
                {description}
                {bottom_description_key_list.map((key, index) => (
                  <ExtraDescriptionContainer
                    key={`${key}-${index}`}
                    extra_description_list={extra_description_list}
                    display_description_key={key}
                    style={{ paddingBottom: 24 }}
                  />
                ))}
              </div>
            </Wrap>
          </TransformComponent>
        </TransformWrapper>
        {crema_fit_url_list.map((url, index) => (
          <Iframe key={index} src={url} title='cremafit' />
        ))}
        {Boolean(purchase_notice) && (
          <PurchaseNotice title={purchase_notice?.title ?? ''} content_list={purchase_notice?.content_list ?? []} />
        )}
      </GoodsExtraInfo>
    </FoldableComponent>
  );
};

const DescriptionLoading = styled.div`
  width: 100%;
  height: 1600px;
`;

const GoodsExtraInfo = styled.div<{ isZbrowser: boolean; is_raffle?: boolean }>`
  padding: ${({ is_raffle }) => (is_raffle ? '0' : '24px 0 40px 0')};
  text-align: center;

  .react-transform-wrapper {
    width: 100%;
    height: auto;
  }

  .react-transform-component {
    width: 100%;
    height: auto;
    transition-timing-function: cubic-bezier(0.1, 0.57, 0.1, 1);
    transition-duration: 0ms;
  }

  iframe {
    max-width: 100%;
    border: none;
  }

  embed {
    max-width: 100%;
    max-height: calc(100vw / (16 / 9));
  }

  code {
    width: 100%;
    white-space: pre-wrap;
  }

  pre {
    width: 100%;
    white-space: pre-wrap;
  }

  b {
    font-weight: bold;
  }

  ${({ isZbrowser }) =>
    !isZbrowser &&
    `
    ul {
      margin-top: 6px;
      list-style: initial;
  
      li {
        padding-left: 2px;
        margin-left: 20px;
        margin-bottom: 10px;
        &:last-child {
          margin-bottom: 0;
        }
      }
    }
    h4 {
      font-size: 20px;
    }
  `}
  .pdp-image {
    position: relative;

    .lazy {
      opacity: 0;
    }
  }

  .swiper-container {
    position: relative;
    overflow: hidden;
  }
`;

const Wrap = styled.div<{
  inlineStyle?: SerializedStyles | null;
  extraStyle?: SerializedStyles | null;
  defaultStyle: SerializedStyles | null;
  is_raffle?: boolean;
}>`
  padding: ${({ is_raffle }) => (is_raffle ? '0' : '0 16px')};
  width: 100%;

  ${({ inlineStyle }) => inlineStyle};

  ${({ extraStyle }) => extraStyle};

  ${({ defaultStyle }) => defaultStyle}
`;

const Iframe = styled.iframe`
  width: 100%;
  height: 50vh;
  border: none;
`;
